/**
 * Utility functions for formatting data
 */

/**
 * Format number as Nigerian Naira currency
 * @param {number} amount - The amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '₦0.00';

  const num = parseFloat(amount);
  if (isNaN(num)) return '₦0.00';

  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
    .format(num)
    .replace('NGN', '₦');
};

/**
 * Format number with commas
 * @param {number} num - The number to format
 * @returns {string} Formatted number string
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';

  const number = parseFloat(num);
  if (isNaN(number)) return '0';

  return new Intl.NumberFormat('en-US').format(number);
};

/**
 * Abbreviate large numbers (1K, 1M, 1B)
 * @param {number} num - The number to abbreviate
 * @returns {string} Abbreviated number string
 */
export const abbreviateNumber = (num) => {
  if (num === null || num === undefined) return '0';

  const number = parseFloat(num);
  if (isNaN(number)) return '0';

  if (number >= 1000000000) {
    return (number / 1000000000).toFixed(1) + 'B';
  }
  if (number >= 1000000) {
    return (number / 1000000).toFixed(1) + 'M';
  }
  if (number >= 1000) {
    return (number / 1000).toFixed(1) + 'K';
  }
  return number.toString();
};

/**
 * Format percentage
 * @param {number} value - The percentage value
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined) return '0%';

  const num = parseFloat(value);
  if (isNaN(num)) return '0%';

  return num.toFixed(decimals) + '%';
};

/**
 * Format relative time (e.g., "2 hours ago", "yesterday")
 * @param {string|Date} date - The date to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  if (!date) return '';

  const now = new Date();
  const past = new Date(date);
  const diffInMs = now - past;
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInSeconds < 60) {
    return 'just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
  } else if (diffInDays === 1) {
    return 'yesterday';
  } else if (diffInDays < 7) {
    return `${diffInDays} days ago`;
  } else {
    return past.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
};

/**
 * Format date as short date string
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return '';

  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format date as YYYY-MM-DD in LOCAL time (safe for API filters)
 * @param {Date|string} date - The date to format
 * @returns {string} Formatted date string (YYYY-MM-DD)
 */
export const formatDateToISO = (date) => {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format date and time
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date) => {
  if (!date) return '';

  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Calculate change percentage
 * @param {number} current - Current value
 * @param {number} previous - Previous value
 * @returns {string} Change percentage string with sign
 */
export const calculateChangePercentage = (current, previous) => {
  if (!previous || previous === 0) return '+0.0%';

  const change = ((current - previous) / previous) * 100;
  const sign = change >= 0 ? '+' : '';

  return `${sign}${change.toFixed(1)}%`;
};

/**
 * Get change type based on current and previous values
 * @param {number} current - Current value
 * @param {number} previous - Previous value
 * @returns {'positive'|'negative'|'neutral'} Change type
 */
export const getChangeType = (current, previous) => {
  if (!previous || current === previous) return 'neutral';
  return current > previous ? 'positive' : 'negative';
};
